import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../contexts/ApiContext';
import { mapResponse } from './helpers';
import { useEffect } from 'react';
import {
  ApiCreateStandingOrderDestinationRequest,
  ApiCreateStandingOrderRequest,
  ApiUpdateStandingOrderRequest,
  ApiUpdateStandingOrderStatusRequest,
} from '../openapi';
import { useAccountsQuery } from './accounts';
import { isFiatCurrency } from '@razz-team/utils';
import { mapValues, uniq } from 'lodash-es';

export function useStandingOrdersListQuery() {
  const client = useApiClient();

  const query = useQuery({
    queryKey: ['standing-orders'],
    queryFn: ({ signal }) =>
      client
        .GET('/standingOrders', {
          signal,
        })
        .then(mapResponse)
        .then(({ orders }) => orders),
    retry: true,
    refetchInterval: 10_000,
  });

  const queryClient = useQueryClient();
  useEffect(() => {
    if (query.data) {
      for (const it of query.data) {
        queryClient.setQueryData(['standing-orders', it.id], it);
      }
    }
  }, [query.data, queryClient]);

  return query;
}

export function useStandingOrderQuery({ orderId }: { orderId: string }) {
  const client = useApiClient();

  return useQuery({
    queryKey: ['standing-orders', orderId],
    queryFn: ({ signal }) =>
      client
        .GET('/standingOrders/{id}', { signal, params: { path: { id: orderId } } })
        .then(mapResponse)
        .then(({ order }) => order),
  });
}

export function useStandingOrderDestinationsQuery({ orderId }: { orderId: string }) {
  const client = useApiClient();

  return useQuery({
    queryKey: ['standing-orders', orderId, 'destinations'],
    queryFn: ({ signal }) =>
      client
        .GET('/standingOrders/{id}/destinations', {
          signal,
          params: { path: { id: orderId } },
        })
        .then(mapResponse)
        .then(({ destinations }) => destinations.sort((a, b) => b.ratio - a.ratio)),
  });
}

export function useCreateStandingOrderMutation({ idempotencyKey }: { idempotencyKey: string }) {
  const client = useApiClient();

  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ApiCreateStandingOrderRequest) =>
      client
        .POST('/standingOrders', { body, headers: { 'Idempotency-Key': idempotencyKey } })
        .then(mapResponse)
        .then(({ order }) => order),
    onSuccess: async (data) => {
      queryClient.setQueryData(['standing-orders', data.id], data);
      await queryClient.invalidateQueries({ queryKey: ['standing-orders'] });
    },
  });
}

export function useStandingOrderOptions() {
  const client = useApiClient();
  const accountsQuery = useAccountsQuery();
  return useQuery({
    queryKey: ['standing-order-options', accountsQuery.data],
    queryFn: async () => {
      const { accounts } = await client.GET('/accounts').then(mapResponse);

      const currencyToNetworks: Record<string, string[]> = {};
      await Promise.all(
        accounts.map(async (account) => {
          const currency = account.balance.currency;
          const isFiat = isFiatCurrency(currency);
          const networks = (currencyToNetworks[currency] = currencyToNetworks[currency] ?? []);

          const { addresses } = await client
            .GET('/accounts/{id}/addresses', { params: { path: { id: account.id } } })
            .then(mapResponse);

          for (const address of addresses) {
            if (address.designation === 'VOID') continue;
            if (isFiat) {
              networks.push(...(address.supportedNetworks ?? []));
            } else {
              networks.push(address.type);
            }
          }
        }),
      );

      return mapValues(currencyToNetworks, uniq);
    },
  });
}

export function useCreateStandingOrderDestinationMutation({
  orderId,
  idempotencyKey,
}: {
  orderId: string;
  idempotencyKey: string;
}) {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['standing-orders', orderId, 'destinations', idempotencyKey],
    mutationFn: (body: ApiCreateStandingOrderDestinationRequest) =>
      client
        .POST('/standingOrders/{id}/destinations', {
          body,
          params: { path: { id: orderId } },
          headers: { 'Idempotency-Key': idempotencyKey },
        })
        .then(mapResponse)
        .then(({ destination }) => destination),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['standing-orders', orderId, 'destinations'],
      });
    },
  });
}

export function useUpdateStandingOrderMutation({ orderId }: { orderId: string }) {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['standing-orders', orderId, 'status'],
    mutationFn: (body: ApiUpdateStandingOrderRequest) =>
      client.PUT('/standingOrders/{id}', {
        params: { path: { id: orderId } },
        body: body,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['standing-orders'] });
    },
  });
}

export function useUpdateStandingOrderStatusMutation({ orderId }: { orderId: string }) {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['standing-orders', orderId, 'status'],
    mutationFn: (body: ApiUpdateStandingOrderStatusRequest) =>
      client.PUT('/standingOrders/{id}/status', {
        params: { path: { id: orderId } },
        body: body,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['standing-orders'] });
    },
  });
}

export function useTerminateStandingOrderDestinationMutation() {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ orderId, destinationId }: { orderId: string; destinationId: string }) =>
      client.PUT('/standingOrders/{id}/destinations/{destinationId}/terminate', {
        params: { path: { id: orderId, destinationId } },
      }),
    onSuccess: async (_, { orderId }) => {
      await queryClient.invalidateQueries({
        queryKey: ['standing-orders', orderId, 'destinations'],
      });
    },
  });
}
